/**
 * Stream Filter - Multi-checkbox selector for stream names (stdout, stderr)
 */

import React, { useState, useCallback, useRef, useEffect } from "react";

// Hardcoded stream options
const STREAM_OPTIONS = [
  { value: '1', label: 'stderr' },
  { value: '2', label: 'stdout' },
];

export interface StreamFilterProps {
  streams: string[]; // Available streams from messages
  selectedStreams: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
  allSelected: boolean;
  onAllSelectedChange: (allSelected: boolean) => void;
}

export function StreamFilter(props: StreamFilterProps) {
  const { streams, selectedStreams, onSelectionChange, allSelected, onAllSelectedChange } = props;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback((stream: string) => {
    const newSelected = new Set(selectedStreams);
    if (newSelected.has(stream)) {
      newSelected.delete(stream);
    } else {
      newSelected.add(stream);
    }
    onSelectionChange(newSelected);
    // If we deselected something, clear the "all selected" flag
    if (newSelected.has(stream)) {
      // We just added it, check if we now have everything
      const availableValues = STREAM_OPTIONS.filter(opt => streams.includes(opt.value)).map(s => s.value);
      if (newSelected.size === availableValues.length) {
        onAllSelectedChange(true);
      }
    } else {
      // We just removed it, clear the flag
      onAllSelectedChange(false);
    }
  }, [selectedStreams, onSelectionChange, onAllSelectedChange, streams]);

  // Filter stream options to only show those that exist in the data
  const availableStreams = STREAM_OPTIONS.filter(opt => streams.includes(opt.value));

  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(availableStreams.map(s => s.value)));
    onAllSelectedChange(true);
  }, [availableStreams, onSelectionChange, onAllSelectedChange]);

  const handleClearAll = useCallback(() => {
    onSelectionChange(new Set());
    onAllSelectedChange(false);
  }, [onSelectionChange, onAllSelectedChange]);

  const selectedCount = selectedStreams.size;
  const totalCount = availableStreams.length;
  const buttonLabel = allSelected
    ? "Streams: All"
    : selectedCount === 0
    ? "Streams: None"
    : `Streams: ${selectedCount}/${totalCount}`;

  // Create tooltip showing selected values
  const tooltipText = allSelected
    ? "All streams selected"
    : selectedCount === 0
    ? "No streams selected"
    : `Selected: ${availableStreams
        .filter(s => selectedStreams.has(s.value))
        .map(s => s.label)
        .join(', ')}`;

  if (availableStreams.length === 0) {
    return null; // Don't show if no streams
  }

  return (
    <div className="stream-filter" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="stream-filter__button"
        title={tooltipText}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <>
          <div className="stream-filter__backdrop" onClick={() => setIsOpen(false)} />
          <div className="stream-filter__menu">
            <div className="stream-filter__header">
              <span>Stream Names</span>
              <div className="stream-filter__actions">
                <button onClick={handleSelectAll} className="stream-filter__action">
                  All
                </button>
                <button onClick={handleClearAll} className="stream-filter__action">
                  None
                </button>
              </div>
            </div>
            <div className="stream-filter__items">
              {availableStreams.map(stream => (
                <label key={stream.value} className="stream-filter__item">
                  <input
                    type="checkbox"
                    checked={selectedStreams.has(stream.value)}
                    onChange={() => handleToggle(stream.value)}
                  />
                  <span>{stream.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
